import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  HiOutlineDocumentReport,
  HiOutlineDownload,
  HiOutlineFilter,
  HiOutlineX,
  HiOutlineAcademicCap,
  HiOutlineSearch,
} from "react-icons/hi";
import Pagination from "../components/Pagination";
import {
  generateContinuousReport,
  exportContinuousReportExcel,
} from "../services/reportingApi";
import { fetchMasterData, fetchAssessmentYears } from "../services/masterDataApi";
import { useUserRole } from "../hooks/useUserRole";
import { fetchMyStates, fetchMyBlocks, fetchMyDistricts } from "../services/assignmentApi";
import SearchableDropdown from "../components/SearchableDropdown";

function ContinuousAccelerationReport() {
  const { role, userProfile } = useUserRole();
  const [states, setStates] = useState([]);
  const [pmBlocks, setPmBlocks] = useState([]);
  const [plDistricts, setPlDistricts] = useState([]);
  const [assessmentYears, setAssessmentYears] = useState([]);
  const [cycles, setCycles] = useState([]);

  const [filters, setFilters] = useState({
    state_id: "",
    assessment_year_id: "",
    cycle_id: "",
    district_id: "",
    block_id: "",
    grade: "",
  });


  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchRef = useRef(null);

  // Debounce search - prevents re-render storm that causes focus loss
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load states based on role
  useEffect(() => {
    if (!role) return;
    // ADMIN's State picker is a SearchableDropdown that fetches its own options.
    if (role === "SPL") {
      fetchMyStates().then((myStates) => {
        if (myStates && myStates.length > 0) {
          setStates(myStates);
          setFilters((prev) => ({ ...prev, state_id: String(myStates[0].id) }));
        }
      }).catch(() => {});
    } else if (role === "PL") {
      fetchMyDistricts().then((myDistricts) => {
        if (myDistricts && myDistricts.length > 0) {
          setPlDistricts(myDistricts);
          setFilters((prev) => ({ ...prev, state_id: String(myDistricts[0].state_id) }));
        }
      }).catch(() => {});
    } else if (role === "PM") {
      fetchMyBlocks().then((myBlocks) => {
        if (myBlocks && myBlocks.length > 0) {
          setPmBlocks(myBlocks);
          setFilters((prev) => ({ ...prev, state_id: String(myBlocks[0].state_id) }));
        }
      }).catch(() => {});
    } else if (role === "PO" && userProfile?.state_id) {
      setFilters((prev) => ({ ...prev, state_id: String(userProfile.state_id) }));
    }
  }, [role, userProfile]);

  // Assessment years don't depend on the state — fetch once, show once a
  // state is picked.
  const allYearsRef = useRef(null);
  useEffect(() => {
    if (filters.state_id) {
      if (!allYearsRef.current) {
        allYearsRef.current = fetchAssessmentYears()
          .then((res) => (res.success ? res.data || [] : []))
          .catch(() => { allYearsRef.current = null; return []; });
      }
      allYearsRef.current.then(setAssessmentYears);
    } else {
      setAssessmentYears([]);
      setCycles([]);
    }
  }, [filters.state_id]);

  // The Cycle dropdown is filled from the main report's `available_cycles`
  // (see loadReport) — the backend builds that list from state + year only,
  // before applying the cycle/district/block filters.


  // Generate report - uses debouncedSearch (not searchTerm) to avoid focus loss
  const loadReport = useCallback(async () => {
    if (!filters.state_id) {
      setReportData(null);
      return;
    }
    setLoading(true);
    try {
      const payload = { state_id: Number(filters.state_id), page, page_size: pageSize };
      if (filters.district_id) payload.district_id = Number(filters.district_id);
      if (filters.block_id) payload.block_id = Number(filters.block_id);
      if (filters.assessment_year_id) payload.assessment_year_id = filters.assessment_year_id;
      if (filters.cycle_id) payload.cycle_id = filters.cycle_id;
      if (filters.grade) payload.grade = Number(filters.grade);
      if (debouncedSearch.trim()) payload.search = debouncedSearch.trim();

      const res = await generateContinuousReport(payload);
      if (res.success) {
        setReportData(res.data);
        setCycles(res.data?.available_cycles || []);
      }
    } catch (err) {
      console.error("Report generation failed:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, debouncedSearch]);

  useEffect(() => {
    if (filters.state_id) {
      loadReport();
    }
  }, [loadReport]);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === "state_id") {
      next.assessment_year_id = "";
      next.cycle_id = "";
      next.district_id = "";
      next.block_id = "";
    }
    if (key === "assessment_year_id") {
      next.cycle_id = "";
    }
    if (key === "district_id") {
      next.block_id = "";
    }
    setFilters(next);
    setPage(1);
  };

  const clearFilters = () => {
    const base = { state_id: "", assessment_year_id: "", cycle_id: "", district_id: "", block_id: "", grade: "" };
    if (role === "PM" && pmBlocks.length > 0) {
      base.state_id = String(pmBlocks[0].state_id);
    } else if (role === "PL" && plDistricts.length > 0) {
      base.state_id = String(plDistricts[0].state_id);
    } else if (role === "PO" && userProfile?.state_id) {
      base.state_id = String(userProfile.state_id);
    } else if (role === "SPL") {
      base.state_id = filters.state_id;
    }
    setFilters(base);
    setPage(1);
    setSearchTerm("");
    setDebouncedSearch("");
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = {};
      if (filters.state_id) payload.state_id = Number(filters.state_id);
      if (filters.district_id) payload.district_id = Number(filters.district_id);
      if (filters.block_id) payload.block_id = Number(filters.block_id);
      if (filters.assessment_year_id) payload.assessment_year_id = filters.assessment_year_id;
      if (filters.cycle_id) payload.cycle_id = filters.cycle_id;
      if (filters.grade) payload.grade = Number(filters.grade);

      const blob = await exportContinuousReportExcel(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "school_wise_report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = Object.values(filters).some((v) => v !== "");
  const schools = reportData?.schools || [];
  const competencies = reportData?.competencies || [];
  const categories = reportData?.categories || [];
  const pagination = reportData?.pagination || {};

  const selectCls =
    "h-[34px] border border-[#d0d5dd] rounded-lg px-2.5 text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all appearance-none w-full";

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[18px] font-bold text-[#24292f] flex items-center gap-2">
            <HiOutlineDocumentReport className="text-[#0078d4]" />
            School Wise Report
          </h1>
          <p className="text-[12px] text-[#57606a] mt-0.5">
            School-wise report for continuous cycle assessments
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || schools.length === 0}
          className="inline-flex items-center gap-2 h-[36px] px-4 text-[12.5px] font-medium rounded-lg cursor-pointer bg-[#0078d4] text-white border-none hover:bg-[#106ebe] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HiOutlineDownload className="text-sm" />
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#e5e7eb] mb-5">
        <div className="px-5 py-3 border-b border-[#f0f0f0] flex items-center gap-2">
          <HiOutlineFilter className="text-[#8b949e]" />
          <span className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">
            Filters
          </span>
        </div>
        <div className="px-5 py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">State</label>
              {role === "PM" ? (
                <select value={filters.state_id} onChange={(e) => handleFilterChange("state_id", e.target.value)} className={selectCls}>
                  {[...new Map(pmBlocks.map(b => [b.state_id, { id: b.state_id, name: b.state_name }])).values()]
                    .map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              ) : role === "SPL" ? (
                <select value={filters.state_id} onChange={(e) => handleFilterChange("state_id", e.target.value)} className={selectCls}>
                  {states.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              ) : role === "PL" ? (
                <select value={filters.state_id} onChange={(e) => handleFilterChange("state_id", e.target.value)} className={selectCls}>
                  {[...new Map(plDistricts.map(d => [d.state_id, { id: d.state_id, name: d.state_name }])).values()]
                    .map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              ) : role === "PO" ? (
                <div className={selectCls + " bg-[#f6f8fa] text-[#57606a] flex items-center"}>
                  {states.find(s => String(s.id) === filters.state_id)?.name || (filters.state_id ? `State #${filters.state_id}` : "—")}
                </div>
              ) : (
                <SearchableDropdown
                  value={String(filters.state_id || "")}
                  onChange={(id) => handleFilterChange("state_id", id)}
                  placeholder="Select State"
                  fetchFn={(search, page = 1) => fetchMasterData("states", {
                    page, page_size: 20, search,
                    has_assessment: true,
                    cycle_type: "continuous",
                  })}
                  variant="report"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Assessment Year</label>
              <select value={filters.assessment_year_id} onChange={(e) => handleFilterChange("assessment_year_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">Select Assessment Year</option>
                {assessmentYears.map((ay) => (<option key={ay.id} value={ay.id}>{ay.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle / Round</label>
              <select value={filters.cycle_id} onChange={(e) => handleFilterChange("cycle_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">All Cycles</option>
                {cycles.map((c) => (<option key={c.id} value={c.id}>{c.title || `Round ${c.round_number}`}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">District</label>
              {(role === "PM" || role === "PL") ? (
                <select value={filters.district_id} onChange={(e) => handleFilterChange("district_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                  <option value="">All Districts</option>
                  {(role === "PM"
                    ? [...new Map(pmBlocks.map(b => [b.district_id, {id: b.district_id, name: b.district_name}])).values()]
                    : plDistricts
                  ).slice().sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <SearchableDropdown
                  key={`car-dist-${filters.state_id}-${filters.cycle_id || "all"}`}
                  value={filters.district_id}
                  onChange={(id) => handleFilterChange("district_id", id)}
                  placeholder="All Districts"
                  fetchFn={(search, page = 1) => fetchMasterData("districts", {
                    page, page_size: 20,
                    state_id: Number(filters.state_id),
                    search,
                    has_assessment: true,
                    cycle_id: filters.cycle_id || undefined,
                    cycle_type: "continuous",
                  })}
                  disabled={!filters.state_id}
                  variant="report"
                  emptyMessage="No districts with completed assessments"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Block</label>
              {role === "PM" ? (
                <select value={filters.block_id} onChange={(e) => handleFilterChange("block_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                  <option value="">All Blocks</option>
                  {pmBlocks.filter(b => !filters.district_id || String(b.district_id) === String(filters.district_id))
                    .slice().sort((a, b) => a.name.localeCompare(b.name)).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                </select>
              ) : (
                <SearchableDropdown
                  key={`car-blk-${filters.district_id || filters.state_id}-${filters.cycle_id || "all"}`}
                  value={filters.block_id}
                  onChange={(id) => handleFilterChange("block_id", id)}
                  placeholder="All Blocks"
                  fetchFn={(search, page = 1) => fetchMasterData("blocks", {
                    page, page_size: 20,
                    state_id: Number(filters.state_id),
                    ...(filters.district_id ? { district_id: Number(filters.district_id) } : {}),
                    search,
                    has_assessment: true,
                    cycle_id: filters.cycle_id || undefined,
                    cycle_type: "continuous",
                  })}
                  disabled={!filters.state_id}
                  variant="report"
                  emptyMessage="No blocks with completed assessments"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Grade</label>
              <select value={filters.grade} onChange={(e) => handleFilterChange("grade", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">All Grades</option>
                {[1,2,3,4,5,6,7,8].map((g) => (<option key={g} value={g}>Grade {g}</option>))}
              </select>
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3">
              <button onClick={clearFilters} className="inline-flex items-center gap-1 h-[30px] px-3 text-[11px] font-medium rounded-lg cursor-pointer bg-[#f3f4f6] text-[#57606a] border border-[#d0d5dd] hover:bg-[#e5e7eb] transition-all">
                <HiOutlineX className="text-xs" /> Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-[#e5e7eb] rounded w-48 mx-auto" />
            <div className="h-40 bg-[#e5e7eb] rounded" />
          </div>
        </div>
      ) : !filters.state_id ? (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
          <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
          <p className="text-[13px] text-[#57606a]">Select a state to generate the report.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#f8f9fa] flex items-center justify-between gap-4">
            <h3 className="text-[13px] font-bold text-[#24292f] uppercase tracking-wider whitespace-nowrap">
              School-wise Report ({pagination.total_count || 0} schools)
            </h3>
            <div className="relative flex-1 max-w-md">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e] text-sm" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by school name, UDISE, district, block..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-[34px] w-full border border-[#d0d5dd] rounded-lg pl-9 pr-3 text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all"
              />
            </div>
          </div>
          {schools.length === 0 ? (
            <div className="p-10 text-center">
              <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">No data found. Try adjusting your filters or search.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-[#f6f8fa] border-b-2 border-[#d0d5dd]">
                  <th className="text-left px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide sticky left-0 bg-[#f6f8fa] z-10">School</th>
                  {competencies.map((comp, i) => (
                    <th key={comp.code} className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide whitespace-nowrap" title={comp.name}>
                      {comp.code || `COMP${String(i + 1).padStart(3, '0')}`}
                    </th>
                  ))}
                  <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Avg Score</th>
                  {categories.map((cat) => (
                    <th key={cat.name} className="text-center px-3 py-3 text-[11px] font-bold uppercase tracking-wide" style={{ color: cat.color || "#24292f" }}>
                      Category {cat.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e7eb]">
                {schools.map((school, idx) => (
                  <tr key={school.school_id || idx} className="hover:bg-[#f6f8fa] transition-colors">
                    <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-medium sticky left-0 bg-white z-10 whitespace-nowrap">{school.school_name}</td>
                    {competencies.map((comp) => {
                      const compData = school.competency_scores?.[comp.code];
                      const count = typeof compData === 'object' ? (compData?.count || 0) : (compData || 0);
                      return (
                        <td key={comp.code} className="px-3 py-2 text-[12.5px] text-[#57606a] text-center">
                          {count === 0 ? <span className="text-[#8b949e] italic">Not Tested</span> : count}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-semibold text-center">{school.avg_score_numerator}/{school.avg_score_denominator}</td>
                    {categories.map((cat) => (
                      <td key={cat.name} className="px-3 py-2 text-center">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{school[`category_${cat.name.toLowerCase()}`] || 0}</span>
                      </td>
                    ))}
                  </tr>
                ))}
                {schools.length > 0 && (() => {
                  const compTotals = competencies.map((comp) => schools.reduce((sum, s) => { const d = s.competency_scores?.[comp.code]; return sum + (typeof d === 'object' ? (d?.count || 0) : (d || 0)); }, 0));
                  const allCompSum = compTotals.reduce((a, b) => a + b, 0);
                  const avgNum = schools.reduce((s, sc) => s + (sc.avg_score_numerator || 0), 0) / schools.length;
                  const denom = competencies.length;
                  const catTotals = categories.map((cat) => schools.reduce((s, sc) => s + (sc[`category_${cat.name.toLowerCase()}`] || 0), 0));
                  const totalStudents = catTotals.reduce((a, b) => a + b, 0);
                  return (
                    <>
                      <tr className="bg-[#f0f4f8] font-bold border-t-2 border-[#d0d5dd]">
                        <td className="px-3 py-2 text-[12px] text-[#24292f] sticky left-0 bg-[#f0f4f8] z-10">Total (Count)</td>
                        {compTotals.map((t, ci) => (<td key={`cnt-${ci}`} className="px-3 py-2 text-[12px] text-[#24292f] text-center">{t || "NA"}</td>))}
                        <td className="px-3 py-2 text-[12px] text-[#24292f] text-center">{avgNum.toFixed(1)}/{denom}</td>
                        {catTotals.map((t, ci) => (<td key={`cat-${ci}`} className="px-3 py-2 text-[12px] text-[#24292f] text-center">{t}</td>))}
                      </tr>
                      <tr className="bg-[#f0f4f8] font-bold">
                        <td className="px-3 py-2 text-[12px] text-[#24292f] sticky left-0 bg-[#f0f4f8] z-10">Total (%)</td>
                        {compTotals.map((t, ci) => (<td key={`pct-${ci}`} className="px-3 py-2 text-[12px] text-[#24292f] text-center">{allCompSum > 0 && t > 0 ? `${Math.round(t / allCompSum * 100)}%` : "NA"}</td>))}
                        <td className="px-3 py-2 text-[12px] text-[#24292f] text-center">{denom > 0 ? `${Math.round(avgNum / denom * 100)}%` : "0%"}</td>
                        {catTotals.map((t, ci) => (<td key={`cpct-${ci}`} className="px-3 py-2 text-[12px] text-[#24292f] text-center">{totalStudents > 0 ? `${Math.round(t / totalStudents * 100)}%` : "0%"}</td>))}
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
            </div>
          )}
          {pagination.total_pages > 1 && schools.length > 0 && (
            <Pagination currentPage={pagination.page} totalPages={pagination.total_pages} totalCount={pagination.total_count} pageSize={pagination.page_size} onPageChange={setPage} entityName="schools" />
          )}
        </div>
      )}
    </div>
  );
}

export default ContinuousAccelerationReport;
